import { EFormTableMode, TApplication } from '@/interfaces'
import { ApplicationsState, FormState } from '@/state'
import { applicationsStateActions, formStateActions } from '@/state/actions'
import { OfflineState } from '@/state/offline-management'
import { useComputed } from '@preact/signals-react'
import { Button, notification, Popconfirm, Row, Tooltip } from 'antd'
import { FaEye } from 'react-icons/fa6'
import { FiRefreshCw } from 'react-icons/fi'
import { MdDelete, MdEditSquare } from 'react-icons/md'
import { canChangeTableData } from '.'

export default function SubsectionTableActions({
  rowInfo,
}: {
  rowInfo: TApplication
}) {
  const handleEditRow = () => {
    formStateActions.openSubsectionForm(EFormTableMode.UPDATE, rowInfo)
  }

  const handleViewRow = () => {
    formStateActions.openSubsectionForm(EFormTableMode.VIEW, rowInfo)
  }

  const handleRemoveRow = () => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      deletingRow: rowInfo.id as string,
    }
    applicationsStateActions
      .deleteRowdata(rowInfo.id as string)
      .then(() =>
        notification.success({
          message: 'Registro eliminado correctamente',
          placement: 'top',
        })
      )
      .catch(() =>
        notification.error({
          message: 'Error eliminando el registro',
          placement: 'top',
        })
      )
      .finally(() => {
        ApplicationsState.loading.value = {
          ...ApplicationsState.loading.peek(),
          deletingRow: false,
        }
      })
  }

  const loadingDelete = useComputed(
    () => ApplicationsState.loading.value.deletingRow === rowInfo.id
  )

  const rowEditable =
    typeof rowInfo.editable === 'boolean' ? rowInfo.editable : true

  const canEdit = useComputed(() =>
    Boolean(
      rowEditable &&
        ApplicationsState.viewMode.value.edit &&
        FormState.currentEntity.value?.permission?.edit &&
        canChangeTableData.value
    )
  )

  const canDelete = useComputed(() =>
    Boolean(
      rowEditable &&
        ApplicationsState.viewMode.value.edit &&
        FormState.currentEntity.value?.permission?.delete &&
        canChangeTableData.value
    )
  )

  if (
    OfflineState.syncAppsIds.value.includes(
      ApplicationsState.currentApplication.value?.id as string
    )
  ) {
    return (
      <Row className='table-actions'>
        <Tooltip title='Sincronizando información'>
          <FiRefreshCw size={20} className='sync-action' />
        </Tooltip>
      </Row>
    )
  }

  return (
    <Row className='table-actions'>
      <Tooltip placement='left' title='Visualizar'>
        <Button
          shape='circle'
          icon={<FaEye size={16} />}
          onClick={handleViewRow}
        />
      </Tooltip>

      {canEdit.value && (
        <Tooltip placement='left' title='Editar'>
          <Button
            shape='circle'
            icon={<MdEditSquare size={16} />}
            onClick={handleEditRow}
          />
        </Tooltip>
      )}

      {canDelete.value && (
        <Tooltip placement='left' title='Eliminar'>
          <Popconfirm
            title='¿Estás seguro de eliminar este registro?'
            okText='Sí, eliminar'
            cancelText='No, cancelar'
            onConfirm={handleRemoveRow}
            okButtonProps={{
              loading: loadingDelete.value,
            }}
            cancelButtonProps={{
              disabled: loadingDelete.value,
            }}
          >
            <Button
              loading={loadingDelete.value}
              className='delete-btn'
              shape='circle'
              icon={<MdDelete size={18} />}
            />
          </Popconfirm>
        </Tooltip>
      )}
    </Row>
  )
}
